import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Cartao, EstadoErro, EstadoVazio, Selo } from '../componentes/ui';
import { requisicaoAutenticada } from '../servicos/http';
import { Pagina } from './Carteira';

interface Pendencia {
  readonly tipo: 'CONTRATO' | 'PRE_CADASTRO' | 'CRONOGRAMA_VENCIDO' | 'SOLICITACAO_FECHAMENTO';
  readonly titulo: string;
  readonly descricao: string;
  readonly quantidade: number;
  readonly destino: string;
}

/**
 * RN-125 — O que está esperando alguém.
 *
 * ## Agregado, não uma caixa de entrada
 *
 * A API responde contagens por tipo, não eventos individuais, e a tela segue
 * esse formato. A pergunta da operação é "o que precisa de mim agora" — e a
 * resposta útil é "7 contratos aguardando o fornecedor", não sete linhas
 * idênticas que alguém tem que contar.
 *
 * Consequência assumida: não há "marcar como lida". Uma pendência sai da lista
 * quando o trabalho é feito, e não quando alguém a dispensa — o que evita a
 * caixa de entrada zerada com sete contratos ainda parados.
 */
export function Notificacoes(): JSX.Element {
  const pendencias = useQuery({
    // Todas — o "Limpar" do sininho não vale aqui: nada foi dado como resolvido.
    queryKey: ['notificacoes', 'todas'],
    queryFn: () => requisicaoAutenticada<Pendencia[]>('/icms/notificacoes?todas=1'),
  });
  // O que os exercícios com SISCRED encerrado deixaram de cobrar (27/10/2026).
  const encerradas = useQuery({
    queryKey: ['notificacoes', 'encerradas'],
    queryFn: () =>
      requisicaoAutenticada<{ ateAno: number | null; quantidade: number }>(
        '/icms/notificacoes/encerradas',
      ),
  });
  const ocultas = encerradas.data;

  if (pendencias.isError) {
    return (
      <Pagina titulo="Notificações">
        <EstadoErro
          titulo="Não foi possível carregar as pendências"
          mensagem={pendencias.error.message}
          aoTentarNovamente={() => void pendencias.refetch()}
        />
      </Pagina>
    );
  }

  const itens = pendencias.data ?? [];
  const total = itens.reduce((soma, p) => soma + p.quantidade, 0);

  return (
    <Pagina
      titulo="Notificações"
      descricao="Tudo o que está parado esperando uma ação, inclusive o que você limpou do sininho. Some da lista quando o trabalho é feito."
      contagem={total}
    >
      {ocultas !== undefined && ocultas.ateAno !== null && ocultas.quantidade > 0 && (
        <p className="mb-4 text-sm text-conteudo-sutil">
          {ocultas.quantidade} pendência(s) de exercícios encerrados (até {ocultas.ateAno}, com o
          SISCRED marcado como encerrado) não aparecem aqui — continuam nas negociações e no
          cronograma, só não são mais cobradas.
        </p>
      )}
      {!pendencias.isPending && itens.length === 0 ? (
        <EstadoVazio
          titulo="Nada pendente"
          descricao="Nenhum contrato, cadastro ou envio esperando ação sua no momento."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {itens.map((p) => (
            <Link
              key={`${p.tipo}:${p.titulo}`}
              to={p.destino}
              className="rounded-card focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
            >
              <Cartao
                como="article"
                titulo={p.titulo}
                acao={<Selo tom={tomDoTipo(p.tipo)}>{p.quantidade}</Selo>}
              >
                <p className="text-sm text-conteudo-secundario">{p.descricao}</p>
              </Cartao>
            </Link>
          ))}
        </div>
      )}
    </Pagina>
  );
}

function tomDoTipo(tipo: Pendencia['tipo']): 'atencao' | 'progresso' | 'critico' {
  // Vencido é o único que já custou prazo — os outros ainda estão dentro do
  // fluxo normal e não merecem a mesma cor.
  if (tipo === 'CRONOGRAMA_VENCIDO') return 'critico';
  if (tipo === 'CONTRATO') return 'progresso';
  return 'atencao';
}
